/**
 * Shared affix display renderers for hero UI.
 * Level 1 (compact): value + stat, color-coded, no names/tiers
 * Level 2 (detailed): value + stat + (name tier), color-coded
 */

import type { AffixInstance, AffixDef, PartyUnit, ConfigData } from '../../types';
import { getTierLabel } from './TooltipContent';
import { formatElement, formatStatLine, formatTagDamageLabel } from './GameTerms';

export interface ParsedAffix {
  instance: AffixInstance;
  def: AffixDef;
}

export interface ParsedUnitAffixes {
  implicit: ParsedAffix | null;
  explicits: ParsedAffix[];
}

/** Parse unit's implicit + explicit affixes into structured data */
export function parseUnitAffixes(unit: PartyUnit, configData: ConfigData | undefined): ParsedUnitAffixes {
  const result: ParsedUnitAffixes = { implicit: null, explicits: [] };
  const affixDefs = configData?.affixes;
  if (!affixDefs) return result;

  if (unit.implicitAffix) {
    try {
      const inst: AffixInstance = typeof unit.implicitAffix === 'string'
        ? JSON.parse(unit.implicitAffix) : unit.implicitAffix;
      const def = affixDefs[inst.affixId];
      if (def) result.implicit = { instance: inst, def };
    } catch { /* ignore */ }
  }

  if (unit.explicitAffixes) {
    try {
      const affixes: AffixInstance[] = typeof unit.explicitAffixes === 'string'
        ? JSON.parse(unit.explicitAffixes) : unit.explicitAffixes;
      for (const inst of affixes) {
        const def = affixDefs[inst.affixId];
        if (def) result.explicits.push({ instance: inst, def });
      }
    } catch { /* ignore */ }
  }

  return result;
}

/** Scaled value for display/math. Affixes with `decimals` store roll as 10^decimals
 *  × the displayed value; this descales for player-facing rendering. */
function scaledValue(inst: AffixInstance, def: AffixDef): number {
  const d = def.decimals ?? 0;
  return d === 0 ? inst.roll : inst.roll / Math.pow(10, d);
}

/** Format the value portion of an affix line (handles all affix categories) */
export function formatAffixValue(inst: AffixInstance, def: AffixDef, configData: ConfigData | undefined): string {
  if (def.category === 'flat_damage') {
    const min = Math.max(1, Math.ceil(inst.roll * 0.5));
    const label = formatElement(def.element!);
    return `Adds ${min} to ${inst.roll} ${label} Damage`;
  }
  if (def.category === 'mastery_cost_reduction') {
    const masteryName = configData?.masteries?.[def.masteryId!]?.name ?? def.masteryId!;
    const reduction = (inst.roll / 100).toFixed(2);
    return `-${reduction}x ${masteryName} Cost`;
  }
  if (def.category === 'tag_increased') {
    return `${inst.roll}% increased ${formatTagDamageLabel(def.conditionTag ?? '')}`;
  }
  if (def.category === 'mastery') {
    const name = configData?.masteries?.[def.masteryId!]?.name ?? def.masteryId!;
    const sign = def.modifier === 'percent' ? '%' : '';
    return `+${inst.roll}${sign} ${name}`;
  }
  return formatStatLine(def.stat, def.modifier, scaledValue(inst, def));
}

/** CSS class for an affix line based on type */
export function affixTypeClass(def: AffixDef): string {
  if (def.type === 'implicit') return 'affix-line--implicit';
  if (def.type === 'prefix') return 'affix-line--prefix';
  return 'affix-line--suffix';
}

/**
 * Level 1: Compact affix lines — value + stat only, color-coded.
 * Options:
 *   maxExplicits — truncate explicits, show "+N more" (default: all)
 */
export function renderAffixesCompact(
  unit: PartyUnit,
  configData: ConfigData | undefined,
  opts?: { maxExplicits?: number },
): string {
  const parsed = parseUnitAffixes(unit, configData);
  if (!parsed.implicit && parsed.explicits.length === 0) return '';

  const lines: string[] = [];

  if (parsed.implicit) {
    const val = formatAffixValue(parsed.implicit.instance, parsed.implicit.def, configData);
    lines.push(`<div class="affix-line ${affixTypeClass(parsed.implicit.def)}">${val}</div>`);
  }

  const max = opts?.maxExplicits ?? parsed.explicits.length;
  const shown = parsed.explicits.slice(0, max);
  const remaining = parsed.explicits.length - shown.length;

  for (const ex of shown) {
    const val = formatAffixValue(ex.instance, ex.def, configData);
    lines.push(`<div class="affix-line ${affixTypeClass(ex.def)}">${val}</div>`);
  }

  if (remaining > 0) {
    lines.push(`<div class="affix-line affix-overflow">+${remaining} more</div>`);
  }

  return `<div class="affix-list">${lines.join('')}</div>`;
}

/**
 * Level 2: Detailed affix lines — value + stat + (name tier), color-coded.
 * Implicit separated from explicits.
 */
export function renderAffixesDetailed(
  unit: PartyUnit,
  configData: ConfigData | undefined,
): string {
  const parsed = parseUnitAffixes(unit, configData);
  if (!parsed.implicit && parsed.explicits.length === 0) return '';

  const lines: string[] = [];

  if (parsed.implicit) {
    const val = formatAffixValue(parsed.implicit.instance, parsed.implicit.def, configData);
    lines.push(`<div class="affix-line ${affixTypeClass(parsed.implicit.def)}">${val} <span class="affix-name">(${parsed.implicit.def.name})</span></div>`);
  }

  if (parsed.implicit && parsed.explicits.length > 0) {
    lines.push(`<div class="affix-separator"></div>`);
  }

  for (const ex of parsed.explicits) {
    const val = formatAffixValue(ex.instance, ex.def, configData);
    const tier = getTierLabel(ex.def, ex.instance.tier ?? 0);
    lines.push(`<div class="affix-line ${affixTypeClass(ex.def)}">${val} <span class="affix-name">(${ex.def.name}${tier})</span></div>`);
  }

  return `<div class="affix-list">${lines.join('')}</div>`;
}
